"use client";
// @ts-strict-ignore
import type { ReactNode } from "react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { CoinMetaList, CoinPriceChartContainer, PulsingDot } from "./style";
import { ChartAlertButton } from "./ChartAlertButton";
import { useSingleAssetPriceFetcher } from "components/pages/prices/useSortedPriceFetcher";
import useChartData from "hooks/useChartData";
import {
  Delta,
  DeltaUtils,
  Flex,
  mediaQuery,
  PriceRolodex,
  SectionMessage
} from "@hubble/web";
import { Chart, ChartLockup } from "@hubble/web/chart";
import type { LineData, MouseEventParams } from "lightweight-charts";
import { format } from "date-fns";
import { useMedia } from "react-use";
import {
  IconGraphBar,
  IconGraphLine,
  IconArrowUpRight,
  IconNotificationAddOutlined,
  IconStarOutlined,
  IconStepperInProgress
} from "@hubble/icons";
import get from "lodash/get";
import { formatMarketCap, formatSupply } from "utils/currencies/formatters";
import { formatVolume } from "components/pages/Derivatives/sections/utils";
import Image from "next/image";
import { getIcon } from "utils/currencies/icon";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { createPriceFormatter, createYAxisFormatter } from "utils/coinPrices";
import { NON_EXCHANGE_TIMEFRAMES, TIMEFRAMES } from "constants/prices";
import type { CoinMetaDataListItemProps } from "types/prices";
import CoinMetaDataListItem from "./CoinMetaDataListItem";
import { useRouter } from "i18n/routing";
import ChartAlertButtonTooltipWrapper from "./ChartAlertButtonTooltipWrapper";
import ChartSocialShareButton from "./ChartSocialShareButton";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { OPTIMIZELY_FEATURE_FLAGS } from "analytics/optimizely/config";
import { getCoinGeckoId } from "utils/getCoinGeckoID";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { isNonEmptyArray } from "utils/common";
import EmptyCoinPriceChart from "./EmptyCoinPriceChart";
import { useWidthObserver } from "hooks/useWidthObserver";
import ChartHeader from "./ChartHeader";
import { Body } from "app/_components/components/Text/Body";
import { Heading } from "app/_components/components/Text/Heading";

// default to line
const CHART_VARIANT = "line";
const getTimeframes = (
  hasAllTimeData: boolean,
  isExchangeToken: boolean,
  isTokenizedStock: boolean = false,
  currentTimeframe: string,
  isLoading: boolean = false
) => {
  // Always use TIMEFRAMES during loading to show all options
  let timeframes = [];
  if (isLoading || isExchangeToken || isTokenizedStock) {
    timeframes = TIMEFRAMES;
  } else {
    timeframes = NON_EXCHANGE_TIMEFRAMES;
  }

  // Transform timeframes to show "LIVE" instead of "1h"
  // Make all timeframes consistent object structure for UI components
  const displayTimeframes = timeframes.map((tf) =>
    tf === "1h"
      ? {
          option: "1h",
          children: (
            <Flex align="center">
              <PulsingDot active={currentTimeframe === "1h"} />
              LIVE
            </Flex>
          )
        }
      : { option: tf, children: tf }
  );

  const allTimeframe = hasAllTimeData
    ? { option: "all", children: "all" }
    : null;
  return allTimeframe
    ? [...displayTimeframes, allTimeframe]
    : displayTimeframes;
};

const CoinPriceChart = ({ currentCoinDetail, initialOverview }) => {
  const router = useRouter();
  const t = useTranslations(FILES.PRICES);
  const { isUK } = useCountryStateCookies();
  const {
    priceData,
    timeframe,
    requestedTimeframe,
    setTimeframe,
    precision,
    isLoading
  } = useSingleAssetPriceFetcher({
    coinOverview: initialOverview
  });

  useEffect(() => {
    // setting this needs to happen on client since the hook will use initial state on server
    if (isUK) {
      setTimeframe("1y");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUK]);
  const { featureFlags, featureVariables } = useFeatureFlags();
  const { isDesktop } = useWidthObserver();

  const shouldShowSocialShare = get(
    featureFlags,
    OPTIMIZELY_FEATURE_FLAGS.IS_PRICE_SOCIAL_SHARE_ENABLED,
    false
  );
  const isCoinGeckoAllTimeEnabled = get(
    featureFlags,
    OPTIMIZELY_FEATURE_FLAGS.IS_COINGECKO_ALL_TIME_ENABLED,
    false
  );

  const isMarsChartEnabled = get(
    featureFlags,
    OPTIMIZELY_FEATURE_FLAGS.HUBBLE_MARS_CHART,
    false
  );

  const scheme = isMarsChartEnabled ? "mars" : undefined;

  // precision-aware price formatter
  const formatters = useMemo(
    () => ({
      yAxis: createYAxisFormatter(precision),
      price: createPriceFormatter(precision)
    }),
    [precision]
  );
  const isCurrencyHighPrecision = precision > 8;

  // reusable function to format time in 12-hour format
  function formatTimeIn12HourFormat(time: number) {
    // Convert the timestamp to a Date object
    const date = new Date(time * 1000);

    // Format the time using date-fns
    const formattedTime = format(date, "h:mm a");

    return formattedTime;
  }

  // Define a stable reference to the timeScaleOptions
  const customTimeScaleOptions = useRef({
    borderVisible: true,
    timeVisible: true,
    secondsVisible: true,
    tickMarkFormatter: formatTimeIn12HourFormat
  });

  // priceData will always be populated on client for all exchange tokens and non exchange tokens
  // initialOverview is only used on server
  const priceOverview =
    priceData?.priceOverview ?? initialOverview?.priceOverview;

  const isExchangeToken = Boolean(priceData)
    ? priceData?.exchangePrices
    : initialOverview?.exchangePrices;

  const isTokenizedStock = Boolean(priceData?.isTokenizedStock);

  const { displayTimeframe, chartData } = useChartData({
    coinSymbol: initialOverview?.coin,
    timeframe,
    isTokenizedStock,
    priceData,
    initialOverview,
    t
  });

  const isMobileXsDown = useMedia(mediaQuery.mobileXsDown, false);
  const [logicalIndex, setLogicalIndex] = useState<number>(
    () => chartData.length - 1
  );
  const [chartActive, setChartActive] = useState<boolean>(false);

  const currentValue = () => {
    let value: number = 0;

    // If user is hovering/interacting with chart, show chart data at that point
    if (chartActive && chartData[logicalIndex]) {
      value = (chartData[logicalIndex] as LineData)?.value;
    }
    // Otherwise, show the latest chart data point (which includes live data if available)
    else if (chartData.length > 0) {
      value = (chartData[chartData.length - 1] as LineData)?.value;
    }
    // Fall back to last trade price from price overview
    else if (priceOverview.lastTradePrice) {
      value = Number(priceOverview.lastTradePrice);
    }

    return value;
  };

  const { status, label } = DeltaUtils.getDeltaFromChartData(
    chartData,
    CHART_VARIANT,
    formatters.price
  );

  const handleCrosshairMove = useCallback(
    (params: MouseEventParams) => {
      if (
        params.point === undefined ||
        !params.time ||
        params.point.x < 0 ||
        params.point.y < 0
      ) {
        if (chartActive) setChartActive(false);
      } else if (typeof params.logical !== "undefined") {
        if (!chartActive) setChartActive(true);
        setLogicalIndex(params.logical);
      }
    },
    [chartActive]
  );

  const renderCoinMetaList = (
    metaData: CoinMetaDataListItemProps[]
  ): ReactNode => {
    return (
      <CoinMetaList pl={0}>
        {metaData.map((_) => (
          <CoinMetaDataListItem {..._} key={_.label} />
        ))}
      </CoinMetaList>
    );
  };
  useEffect(() => {
    setLogicalIndex(chartData.length - 1);
  }, [chartData]);

  if (!Array.isArray(chartData) || chartData.length === 0) return null;

  if (!currentCoinDetail) {
    return null;
  }

  const redirectToExchange = () => {
    router.push("https://exchange.gemini.com/register");
  };

  const coinGeckoId = getCoinGeckoId(
    initialOverview?.coin,
    featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_TOKEN_MAP]
      ?.additionalTokenMap
  );

  const hasAllTimeData = Boolean(coinGeckoId) && isCoinGeckoAllTimeEnabled;

  const displayTimeframes = getTimeframes(
    hasAllTimeData,
    isExchangeToken,
    isTokenizedStock,
    timeframe,
    isLoading
  );

  const isChartDataPresent = isNonEmptyArray(chartData);
  const chartLockUpCss = isChartDataPresent
    ? `min-height: $540px;`
    : `min-height: none;`;
  const isTrading = get(currentCoinDetail, "isTrading");
  const isCustody = get(currentCoinDetail, "isCustody");

  const coinName = priceData?.name ?? initialOverview?.name;
  const coinSymbol = priceData?.coin ?? initialOverview?.coin;
  const coinIconSymbol =
    priceData?.coin ?? initialOverview?.coin ?? priceData?.to;
  const volume = priceOverview?.volume?.value ?? priceData?.volume?.value;
  const marketCap =
    priceOverview?.marketCap?.value ?? priceData?.marketCap?.value;
  const supply = priceOverview?.supply?.value ?? priceData?.supply;

  return (
    <CoinPriceChartContainer>
      <ChartLockup scheme={scheme} css={chartLockUpCss}>
        <ChartLockup.Header justify="space-between" flex={1} overflow="visible">
          <div style={{ width: "100%" }}>
            {!isTrading && isCustody && !isDesktop && (
              <SectionMessage
                heading={
                  <Body size="md" bold>
                    {currentCoinDetail.coin} {t("delistedTokenTitle")}
                  </Body>
                }
                statusType="info"
                mb={2}
              >
                <Body size="sm">
                  {t("delistedTokenMessage", {
                    coinSymbol: currentCoinDetail.coin
                  })}
                </Body>
              </SectionMessage>
            )}
            <ChartHeader
              left={
                <Image
                  height={40}
                  width={40}
                  style={{ borderRadius: "50%" }}
                  src={getIcon(coinIconSymbol, "circle")}
                  alt={` ${coinName} logo`}
                  priority
                />
              }
              right={
                <React.Fragment>
                  {isChartDataPresent && isTrading && (
                    <div className="flex gap-2 self-center overflow-visible">
                      {shouldShowSocialShare && (
                        <ChartSocialShareButton
                          range={chartData.map((_) => ({
                            x: _.time,
                            y: _.value
                          }))}
                          coin={initialOverview.coin}
                          name={initialOverview.name}
                        />
                      )}
                      <ChartAlertButtonTooltipWrapper
                        overlay={
                          <Body size="sm" bold>
                            {t("addToWatchList")}
                          </Body>
                        }
                      >
                        {({ isTooltipVisible }) => (
                          <ChartAlertButton
                            size="sm"
                            variant="secondary"
                            aria-label="mark favorite button"
                            icon={<IconStarOutlined />}
                            onClick={redirectToExchange}
                            isTooltipVisible={isTooltipVisible}
                          />
                        )}
                      </ChartAlertButtonTooltipWrapper>
                      <ChartAlertButtonTooltipWrapper
                        overlay={
                          <Body size="sm" bold>
                            {t("createPriceAlert")}
                          </Body>
                        }
                      >
                        {({ isTooltipVisible }) => {
                          return (
                            <ChartAlertButton
                              size={"sm"}
                              aria-label="create alert button"
                              icon={<IconNotificationAddOutlined />}
                              onClick={redirectToExchange}
                              isTooltipVisible={isTooltipVisible}
                            />
                          );
                        }}
                      </ChartAlertButtonTooltipWrapper>
                    </div>
                  )}
                </React.Fragment>
              }
            >
              <Heading size="xs" as="h1">
                {coinName}
              </Heading>
              <Heading size="xs" as="h1" className="text-[#8F8F93]">
                {coinSymbol}
              </Heading>
            </ChartHeader>
            {isChartDataPresent && (
              <ChartLockup.Hud.Simple
                price={
                  <Heading
                    size="xl"
                    as="div"
                    variant="marketing"
                    className="inline font-semibold"
                  >
                    <PriceRolodex
                      formatter={formatters.price}
                      price={currentValue()}
                    />
                  </Heading>
                }
              >
                <Delta.Summary
                  label={label}
                  status={status}
                  timeRange={displayTimeframe}
                />
              </ChartLockup.Hud.Simple>
            )}
          </div>
        </ChartLockup.Header>
        <div
          style={
            isChartDataPresent
              ? {
                  minHeight: "312px" // based on the height of the chart as rendered on the client
                }
              : { height: "256px" }
          }
          onMouseLeave={() => setLogicalIndex(chartData.length - 1)}
        >
          {isChartDataPresent && (
            <ChartLockup.ChartWrapper scheme={scheme}>
              <Chart
                variant={CHART_VARIANT}
                seriesData={chartData}
                hideTimeScale={isMobileXsDown}
                subscribeCrosshairMove={handleCrosshairMove}
                hideRightPriceScale={isMobileXsDown || isCurrencyHighPrecision}
                interval={timeframe}
                priceFormatter={formatters.yAxis}
                timeScaleOptions={customTimeScaleOptions.current}
                scheme={scheme}
              />
            </ChartLockup.ChartWrapper>
          )}
          {!isChartDataPresent && <EmptyCoinPriceChart />}
        </div>

        {isChartDataPresent && displayTimeframes.length && (
          <ChartLockup.Footer
            justify="space-between"
            css={{
              flexDirection: "column",
              "@media (min-width: 1024px)": { flexDirection: "row" }
            }}
          >
            <ChartLockup.Control
              options={displayTimeframes}
              selectedOption={isLoading ? requestedTimeframe : timeframe}
              onClick={(option) => (isLoading ? null : setTimeframe(option))}
              css={`
                button {
                  text-transform: uppercase;
                  ${isLoading ? "pointer-events: none; opacity: 0.9;" : ""}
                }
              `}
            />
          </ChartLockup.Footer>
        )}
      </ChartLockup>

      <div className="my-[16px] flex w-full flex-row flex-wrap max-[360px]:gap-0 [@media(min-width:599px)]:gap-4 [@media(min-width:599px)]:px-[16px] [@media(min-width:710px)]:my-[48px] [@media(min-width:710px)]:px-[48px] [@media(min-width:904px)]:px-0">
        {renderCoinMetaList([
          {
            icon: <IconGraphBar />,
            label: t("volume"),
            value: volume ? formatVolume(volume) : "--"
          }
        ])}
        {renderCoinMetaList([
          {
            icon: <IconStepperInProgress />,
            label: t("circulatingSupply"),
            value: supply ? formatSupply(supply) : "--"
          },
          {
            icon: <IconGraphLine />,
            label: t("marketCap"),
            value: marketCap ? formatMarketCap(marketCap) : "--"
          }
        ])}
      </div>
    </CoinPriceChartContainer>
  );
};

export default CoinPriceChart;
